import { Fragment, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ApplyPlanBar from './ApplyPlanBar';
import MarketingPctEditor from './MarketingPctEditor';
import PromoBand from './PromoBand';
import { cellOverride, effectiveBudget } from './budgetOverrides';
import { DayHeader, FROZEN, isWeekend } from './tableChrome';
import {
  ROW_META,
  columnTotals,
  cumulativeRealPct,
  dayLabel,
  deltaTone,
  formatMoneyCell,
  formatPct,
  isPastDate,
} from './mediaPlanningView';
import { useCreateOverrideMutation, useDeleteOverrideMutation } from '../../queries/mediaPlanning';

const TONE_CLS = { positive: 'text-emerald-600', negative: 'text-rose-500', neutral: 'text-gray-300' };
const todayIso = () => new Date().toISOString().slice(0, 10);

const monthTitle = (month) => {
  const label = new Date(`${month}-01T00:00:00Z`).toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-card bg-gray-50 px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{value}</div>
    </div>
  );
}

SummaryCard.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired };

function RowLabel({ meta, className = '' }) {
  return (
    <span
      title={meta.tip}
      className={`cursor-help underline decoration-dotted decoration-gray-300 underline-offset-2 ${className}`}
    >
      {meta.label}
    </span>
  );
}

RowLabel.propTypes = { meta: PropTypes.object.isRequired, className: PropTypes.string };

const CELL = 'border-b border-gray-100 px-3.5 py-2.5';
const LABEL = `${FROZEN} z-20 whitespace-nowrap border-b border-gray-100 px-4 py-2.5 text-left`;

export default function MediaPlanningGrid({ plan, brandName }) {
  const { brand_id: brandId, days = [], currency, marketing_pct: marketingPct, month, platforms = [], promos = [] } = plan;
  const today = todayIso();
  const createOverride = useCreateOverrideMutation(brandId, month);
  const deleteOverride = useDeleteOverrideMutation(brandId, month);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [showCampaigns, setShowCampaigns] = useState(true);
  const cancelRef = useRef(false);
  const busy = createOverride.isPending || deleteOverride.isPending;

  const totals = columnTotals(days, ['revenue_goal', 'should_spend', 'real']);
  const realPct = cumulativeRealPct(days);
  const campaignRows = platforms.flatMap((p) => p.campaigns.map((c) => ({ platform: p.platform, campaign: c })));

  const startEdit = (entity, date, amount) => {
    if (busy) return;
    setEditing({ key: entity.key, date, entity });
    setDraft(amount == null ? '' : String(amount));
  };

  const crossEdit =
    editing?.entity && editing.entity.currency && editing.entity.currency !== currency
      ? editing.entity
      : null;
  const nativeEquiv = crossEdit ? (parseFloat(draft) || 0) * (crossEdit.display_to_native || 1) : 0;

  const save = async (entity, date) => {
    setEditing(null);
    const value = parseFloat(draft);
    if (Number.isNaN(value) || value <= 0) return;
    const existing = cellOverride(date, entity.overrides);
    try {
      if (existing) await deleteOverride.mutateAsync(existing.id);
      if (value !== entity.base) {
        await createOverride.mutateAsync({
          platform: entity.platform, campaign_id: entity.campaign_id, group_id: entity.group_id,
          from_date: date, to_date: date, amount: value, source: 'manual',
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo guardar el ajuste');
    }
  };

  const clear = async (existing) => {
    try {
      await deleteOverride.mutateAsync(existing.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo limpiar el ajuste');
    }
  };

  const cellBg = (date) =>
    date === today ? 'bg-indigo-50' : isWeekend(dayLabel(date).weekday) ? 'bg-gray-50/60' : '';

  // The per-day effective budget of an ABO campaign = sum of its ad sets' effective budgets.
  const adsetsSumOn = (adsets, dateIso) =>
    adsets.reduce((sum, a) => sum + (effectiveBudget(dateIso, a.daily_budget, a.overrides).amount || 0), 0);

  // Editable per-day cells for one planning entity (a CBO campaign or an ABO ad set).
  const editableCells = (entity) =>
    days.map((d) => {
      const { amount, overrideId } = effectiveBudget(d.date, entity.base, entity.overrides);
      const owned = cellOverride(d.date, entity.overrides);
      const past = isPastDate(d.date, today);
      const isEditing = editing?.key === entity.key && editing?.date === d.date;
      return (
        <td key={d.date} className={`group relative border-b border-gray-100 px-1.5 py-1 ${cellBg(d.date)}`}>
          {isEditing ? (
            <input
              aria-label={`Presupuesto ${entity.key} ${d.date}`}
              type="number"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => {
                if (cancelRef.current) { cancelRef.current = false; return; }
                save(entity, d.date);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') { cancelRef.current = true; setEditing(null); }
              }}
              className="w-20 rounded border border-primary px-1.5 py-1 text-right text-sm"
            />
          ) : past ? (
            <span className={`block px-2 py-1 text-right tabular-nums ${overrideId ? 'text-amber-700/70' : 'text-gray-400'}`}>
              {formatMoneyCell(amount)}
            </span>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => startEdit(entity, d.date, amount)}
              className={`w-full rounded px-2 py-1 text-right tabular-nums transition-colors ${overrideId ? 'bg-amber-100 font-medium text-amber-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {formatMoneyCell(amount)}
            </button>
          )}
          {owned && !isEditing && !past && (
            <button
              type="button"
              aria-label="Quitar ajuste"
              onClick={() => clear(owned)}
              className="absolute right-0.5 top-0.5 hidden text-xs text-rose-400 hover:text-rose-600 group-hover:block"
            >
              ×
            </button>
          )}
        </td>
      );
    });

  const brandCells = (get, tone) =>
    days.map((d, i) => {
      const value = get(d, i);
      const cls = tone ? TONE_CLS[deltaTone(value)] : value === null || value === undefined ? 'text-gray-300' : 'text-gray-700';
      return (
        <td key={d.date} className={`${CELL} ${cellBg(d.date)}`}>
          <span className={`tabular-nums ${cls}`}>{formatMoneyCell(value)}</span>
        </td>
      );
    });

  const brandRow = (key, { tone } = {}) => (
    <tr key={key}>
      <th className={`${LABEL} font-medium text-gray-700`}>
        <RowLabel meta={ROW_META[key]} />
      </th>
      {brandCells((d) => d[key], tone)}
    </tr>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <h2 className="text-lg font-semibold text-gray-900">{brandName} · {monthTitle(month)}</h2>
          <MarketingPctEditor brandId={brandId} month={month} marketingPct={marketingPct} />
          <span className="text-sm text-gray-500">
            montos en <strong className="font-medium text-gray-700">$ {currency ?? ''}</strong>
          </span>
        </div>
        <ApplyPlanBar brandId={brandId} month={month} />
      </div>

      {crossEdit && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          💱 <strong>{crossEdit.name}</strong> opera en{' '}
          {crossEdit.currency}. Editás en {currency}; en la plataforma se fija ≈{' '}
          <strong>
            {nativeEquiv.toLocaleString('es-ES', { maximumFractionDigits: 2 })} {crossEdit.currency}
          </strong>.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <SummaryCard label="Objetivo de venta (mes)" value={formatMoneyCell(totals.revenue_goal)} />
        <SummaryCard label="Ideal (mes)" value={formatMoneyCell(totals.should_spend)} />
        <SummaryCard label="Real (mes)" value={formatMoneyCell(totals.real)} />
      </div>

      <div className="overflow-x-auto rounded-card border border-gray-200 bg-white shadow-card">
        <table className="w-full border-separate border-spacing-0 text-right text-sm">
          <DayHeader days={days} firstLabel="Concepto" today={today} />
          <tbody>
            <PromoBand brandId={brandId} month={month} promos={promos} days={days} today={today} />
            {brandRow('revenue_goal')}

            <tr className="bg-gray-50/70">
              <th className={`${LABEL} bg-gray-50/70 font-semibold text-gray-800`}>
                <button
                  type="button"
                  onClick={() => setShowCampaigns((v) => !v)}
                  className="flex items-center gap-1"
                >
                  {showCampaigns ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <RowLabel meta={ROW_META.budgeted} className="decoration-gray-400" />
                </button>
              </th>
              {days.map((d) => (
                <td key={d.date} className={`${CELL} ${d.date === today ? 'bg-indigo-50' : 'bg-gray-50/70'} font-semibold text-gray-800`}>
                  <span className="tabular-nums">{formatMoneyCell(d.budgeted)}</span>
                </td>
              ))}
            </tr>

            {showCampaigns && campaignRows.map(({ platform, campaign }) => {
              const key = `${platform}:${campaign.campaign_id}`;
              if (campaign.adsets && campaign.adsets.length > 0) {
                // ABO: the campaign budget lives on its ad sets, so the campaign
                // row is a read-only sum and each ad set is its own (editable) row.
                return (
                  <Fragment key={key}>
                    <tr>
                      <th className={`${LABEL} pl-9 font-normal text-gray-600`}>
                        <div className="flex max-w-[210px] items-center gap-1.5">
                          <span className="truncate">{campaign.campaign_name || campaign.campaign_id}</span>
                          <span className="shrink-0 rounded bg-sky-100 px-1 text-[10px] font-medium text-sky-700">ABO</span>
                        </div>
                        <div className="text-[11px] text-gray-400">suma de conjuntos · {formatMoneyCell(campaign.daily_budget)}</div>
                      </th>
                      {days.map((d) => (
                        <td key={d.date} className={`${CELL} ${cellBg(d.date)}`}>
                          <span className="tabular-nums text-gray-500">{formatMoneyCell(adsetsSumOn(campaign.adsets, d.date))}</span>
                        </td>
                      ))}
                    </tr>
                    {campaign.adsets.map((adset) => (
                      <tr key={`${key}:${adset.group_id}`}>
                        <th className={`${LABEL} pl-14 font-normal text-gray-500`}>
                          <div className="max-w-[200px] truncate text-[13px]">{adset.group_name || adset.group_id}</div>
                          <div className="text-[11px] text-gray-400">base {formatMoneyCell(adset.daily_budget)}</div>
                        </th>
                        {editableCells({
                          key: `${key}:${adset.group_id}`,
                          platform,
                          campaign_id: campaign.campaign_id,
                          group_id: adset.group_id,
                          base: adset.daily_budget,
                          overrides: adset.overrides,
                          name: adset.group_name || adset.group_id,
                          currency: adset.currency,
                          display_to_native: adset.display_to_native,
                        })}
                      </tr>
                    ))}
                  </Fragment>
                );
              }
              return (
                <tr key={key}>
                  <th className={`${LABEL} pl-9 font-normal text-gray-600`}>
                    <div className="max-w-[210px] truncate">{campaign.campaign_name || campaign.campaign_id}</div>
                    <div className="text-[11px] text-gray-400">base {formatMoneyCell(campaign.daily_budget)}</div>
                  </th>
                  {editableCells({
                    key,
                    platform,
                    campaign_id: campaign.campaign_id,
                    group_id: undefined,
                    base: campaign.daily_budget,
                    overrides: campaign.overrides,
                    name: campaign.campaign_name || campaign.campaign_id,
                    currency: campaign.currency,
                    display_to_native: campaign.display_to_native,
                  })}
                </tr>
              );
            })}

            {brandRow('should_spend')}
            {brandRow('real')}
            {brandRow('delta', { tone: true })}

            <tr>
              <th className={`${LABEL} font-medium text-gray-700`}>
                <RowLabel meta={ROW_META.real_pct} />
              </th>
              {days.map((d, i) => (
                <td key={d.date} className={`${CELL} ${cellBg(d.date)}`}>
                  <span className="tabular-nums text-gray-700">{formatPct(realPct[i])}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

MediaPlanningGrid.propTypes = {
  plan: PropTypes.object.isRequired,
  brandName: PropTypes.string,
};
